import React, { useEffect, useState } from 'react';
import { Button, Tooltip } from '@mui/material';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import { useNotify } from 'react-admin';
import { getToken } from '../authentication/authProvider';

const API_URL = process.env.REACT_APP_PAPERMAN_API_URL || 'http://localhost:3001';
const baseUrl = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL;

/**
 * This case's email address (c-xxxx@cases.cleocremation.com), or null while
 * case_email_reply_to_enabled is off — the backend hands out no address then.
 */
export function useCaseEmailAddress(funeralId: number | string | undefined): string | null {
  const [address, setAddress] = useState<string | null>(null);

  useEffect(() => {
    if (funeralId == null) return;
    let cancelled = false;
    void (async () => {
      try {
        const token = await getToken();
        const res = await fetch(
          `${baseUrl}/case-email/address?funeralId=${encodeURIComponent(String(funeralId))}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) return;
        const body = await res.json();
        if (!cancelled) setAddress(body?.address ?? null);
      } catch {
        // No address.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [funeralId]);

  return address;
}

/**
 * Copies this case's email address. Staff writing from their own Gmail BCC it
 * to file the thread on the case (CASE_EMAIL_ADDRESS_PLAN.md §3.2). Renders
 * nothing until the backend hands out an address, i.e. while
 * case_email_reply_to_enabled is off.
 */
export const CopyCaseEmailButton = ({
  funeralId,
  mobile = false,
}: {
  funeralId: number | string;
  mobile?: boolean;
}) => {
  const notify = useNotify();
  const address = useCaseEmailAddress(funeralId);

  if (!address) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(address);
      notify(`Copied ${address}. BCC it to file your email on this case.`, {
        type: 'success',
      });
    } catch {
      notify(address, { type: 'info' });
    }
  };

  return (
    <Tooltip title={`${address}: BCC this from Gmail to file the email on the case`}>
      <Button
        size={mobile ? 'small' : 'medium'}
        onClick={() => void copy()}
        startIcon={mobile ? undefined : <AlternateEmailIcon />}
        fullWidth={mobile}
        sx={mobile ? { justifyContent: 'flex-start' } : undefined}
      >
        {mobile && <AlternateEmailIcon sx={{ mr: 1 }} />}
        Copy case email
      </Button>
    </Tooltip>
  );
};

